import useAmplitude from 'core/hooks/useAmplitude';
import useEventsAnalytics from 'core/hooks/useEventsAnalytics';
import { useTranslate } from 'core/hooks/useTranslate';
import { useCourse } from 'core/lite/hooks/useCourse';
import { parseEducationYearName } from 'core/lite/parsers';
import IframeViewer from 'core/modules/atoms/IframeViewer/IframeViewer';
import { isLessonItemContentType } from 'core/utils/lesson';
import { getTypeNameByContentType } from 'core/utils/lessonItems';
import { isIframeElement } from 'core/utils/utils';
import { AMPLITUDE_EVENT } from 'core/variables/constant';
import { EVENTS_ANALYTICS } from 'core/variables/events-analytics';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { typeContentsIcons } from '../../../variables/constant';
import { DialogImage, IconBubble, TextHtml } from 'ui';
import AudioPlayer from './players/AudioPlayer';
import ImageViewer from './players/ImageViewer';
import VideoPlayer from './players/VideoPlayer';

const PreviewContent = ({
  content,
  type,
  content_guid,
  content_url,
  encoded_url,
  contentUrl,
  contentName,
  contentDescription,
  thumbnail,
  onShowVisor,
  previewImage,
  canOpenImageDialog,
  dialogImage,
  guid,
  measureProgress,
  updateJob,
  courseGuid,
  jobStatus,
  pauseContent,
  showEmbed = false,
}) => {
  const { t } = useTranslate();
  const { sendAmplitudeEvent } = useAmplitude();
  const [showDialogImage, setShowDialogImage] = useState(false);
  const { data: course } = useCourse(courseGuid);
  const shouldMeasureProgress = measureProgress && !course?.isReadOnly;
  const audioRef = useRef();
  const { sendEventAnalytic } = useEventsAnalytics();

  useEffect(() => {
    if (pauseContent && audioRef.current) {
      audioRef.current.pause();
    }
  }, [pauseContent]);

  function renderImage() {
    return (
      <ImageViewer
        guid={guid}
        contentUrl={contentUrl}
        contentName={contentName}
        contentDescription={contentDescription}
        measureProgress={shouldMeasureProgress}
        canOpenImageDialog={canOpenImageDialog}
      />
    );
  }

  function onShowImage() {
    setShowDialogImage(true);
  }

  function onClose() {
    setShowDialogImage(false);
  }

  function onClickShowVisor(event) {
    if (dialogImage && isLessonItemContentType(type, 'image')) {
      onShowImage();
    } else {
      onShowVisor(content, event, contentUrl, type);
    }

    if (isLessonItemContentType(type, 'link') && course?.guid) {
      sendAmplitudeEvent(
        AMPLITUDE_EVENT.OPEN_CONTENT,
        {
          content_type: getTypeNameByContentType(content.content_type_guid),
          content_guid: content.guid,
          discipline: course.discipline_name,
          content_stage: course.education_level_name,
          content_grade: parseEducationYearName(course.education_year_name),
        },
        course
      );

      sendEventAnalytic(
        EVENTS_ANALYTICS.OPEN_CONTENT,
        {
          content_type_guid: content.content_type_guid,
          content_guid: content.guid,
          content_name: content.content_name || content.name,
          is_teacher_only: content.content_is_teacher_only === 0 ? false : true,
        },
        course
      );
    }
  }

  function renderTypeWithIcon() {
    return isIframeElement(content_url) ? (
      <div className="preview-content__iframe">
        <TextHtml text={contentUrl} />
      </div>
    ) : (
      <div className="preview-content__type-with-icon" onClick={onClickShowVisor}>
        <div className="preview-content__type-with-icon-bubble">
          <IconBubble name={typeContentsIcons[type]} size="lg" />
        </div>
        <div className="preview-content__type-with-icon-text-container">
          <span className="preview-content__name">{content?.name}</span>
          {/*<span className="preview-content__description">{contentDescription}</span>*/}
          {type === 'CTTY_03' && <span className="preview-content__duration_video">{content?.contentDurationTime || ''}</span>}
        </div>
      </div>
    );
  }

  function renderTypeLink() {
    // console.log(content);
    return showEmbed ? (
      <IframeViewer
        url={contentUrl}
        title={content?.content_name}
        description={contentDescription}
        contentGuid={content?.content_guid || guid}
        courseGuid={courseGuid}
        jobGuid={content?.job?.id}
      />
    ) : (
      renderTypeWithIcon()
    );
  }

  function renderTypeAudio() {
    return (
      <AudioPlayer
        guid={guid}
        contentUrl={contentUrl}
        contentName={contentName}
        contentDescription={contentDescription}
        measureProgress={shouldMeasureProgress}
        jobStatus={jobStatus}
        updateJob={updateJob}
        contentGuid={content?.content_guid || guid}
      />
    );
    // return (
    //   <audio controls ref={audioRef}>
    //     <source src={contentUrl} type="audio/mpeg" />
    //   </audio>
    // );
  }

  function renderTypeVideo() {
    return (
      <VideoPlayer
        guid={guid}
        content_url={content_url || encoded_url || contentUrl}
        content_guid={content_guid || guid}
        contentUrl={contentUrl}
        contentName={contentName}
        contentDescription={contentDescription}
        thumbnail={thumbnail}
        measureProgress={shouldMeasureProgress}
        jobStatus={jobStatus}
        updateJob={updateJob}
        pauseContent={pauseContent}
      />
    );
  }

  return (
    <div className={`preview-content preview-content__type--${typeContentsIcons[type]}`}>
      {isLessonItemContentType(type, 'image') && previewImage && renderImage()}
      {isLessonItemContentType(type, 'image') && !previewImage && renderTypeWithIcon()}
      {isLessonItemContentType(type, 'video') && contentUrl && renderTypeVideo()}
      {isLessonItemContentType(type, 'link') && renderTypeLink()}
      {(isLessonItemContentType(type, 'pdf') || isLessonItemContentType(type, 'fixed-layout')) && renderTypeWithIcon()}
      {(type === 'CTTY_08' || type === 'CTTY_13') /*|| isLessonItemContentType(type, 'task')*/ && renderTypeWithIcon()}
      {isLessonItemContentType(type, 'audio') && renderTypeAudio()}
      <DialogImage alt={t('alt:Preview of contentType', { contentType: type })} isOpen={showDialogImage} onClose={onClose} src={contentUrl} />
    </div>
  );
};

PreviewContent.propTypes = {
  onShowVisor: PropTypes.func,
  measureProgress: PropTypes.bool,
  canOpenImageDialog: PropTypes.bool,
  content: PropTypes.object,
  type: PropTypes.string,
  content_guid: PropTypes.string,
  content_url: PropTypes.string,
  encoded_url: PropTypes.string,
  contentUrl: PropTypes.string,
  contentName: PropTypes.string,
  contentDescription: PropTypes.string,
  thumbnail: PropTypes.string,
  previewImage: PropTypes.string,
  dialogImage: PropTypes.bool,
  guid: PropTypes.string,
  updateJob: PropTypes.func,
  courseGuid: PropTypes.string,
  jobStatus: PropTypes.string,
  pauseContent: PropTypes.bool,
  showEmbed: PropTypes.bool,
};

PreviewContent.defaultProps = {
  onShowVisor: function () {
    return null;
  },
  measureProgress: true,
  canOpenImageDialog: true,
  updateJob: function () {
    return null;
  },
};

export default PreviewContent;
